import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { withRouter } from "react-router-dom";
import FlexRow from "../../core/FlexRow";
import FlexColumn from "../../core/FlexColumn";
import Icon from "../../core/Icon";
import TranslatedText from "../../core/TranslatedText";
import HomeworkListItemUnitCard from "./HomeworkListItemUnitCard";
import { useEntity } from "student-front-commons/src/hooks";
import { COMPANY_SCHEMA, PROFILE_SCHEMA } from "student-front-commons/src/schemas";

function HomeworkListItem({ homework }) {
  const profile = useEntity(PROFILE_SCHEMA, sessionStorage.getItem("id"));
  const company = useEntity(COMPANY_SCHEMA, profile.company);

  const [date, time, a] = useMemo(() => {
    return moment(homework.dueDate)
      .format(profile?.locale === "pt" ? "DD/MM HH:mm" : "MM/DD/YYYY HH:mm a")
      .split(" ");
  }, [profile, homework]);

  const isLate = useMemo(() => {
    return moment(homework.dueDate).isBefore(moment());
  }, [homework]);
  const isCompleted = useMemo(() => {
    return homework.assigned.every((assign) => assign.lastCompletedExecutionResult?.completedAt);
  }, [homework]);
  const isCompletedAfterDate = useMemo(() => {
    return homework.assigned.some((assign) =>
      moment(assign.lastCompletedExecutionResult?.completedAt).isAfter(moment(homework.dueDate))
    );
  }, [homework]);
  const { text, color } = useMemo(() => {
    let color = "#9fc5f8";
    let text = "available";
    if (isCompleted && !isCompletedAfterDate) {
      color = "#B6D7A8";
      text = "completed";
    }
    if (isCompleted && isCompletedAfterDate) {
      color = "#F9CB9C";
      text = "completedLate";
    }
    if (!isCompleted && isLate) {
      color = "#EA9999";
      text = "late";
    }

    return { color, text };
  }, [isCompleted, isLate]);

  return (
    <FlexColumn gap={8} margin="8px 0">
      <FlexRow columnGap={16}>
        <FlexColumn justifyContent="center">
          <FlexRow alignItems="center" gap={8}>
            <FlexColumn justifyContent="center">
              {isCompleted && <Icon icon="check" size="sm" color="#B6D7A8" />}
              {isLate && !isCompleted && <Icon icon="alert-outline" size="sm" color="#CF2A27" />}
              {!isLate && !isCompleted && <Icon icon="lock-open-outline" size="sm" color="#9fc5f8" />}
            </FlexColumn>
            <h1
              style={{
                margin: 0,
                fontSize: 16,
                lineHeight: "18px",
                fontWeight: 600,
                color: "#424242",
              }}
            >
              {homework.title}
            </h1>
          </FlexRow>
          <FlexRow alignItems="center" gap={8}>
            <TranslatedText
              translateKey="homeworks.list.listItem.dueDateText"
              style={{ fontWeight: 500, fontSize: 14 }}
              as="span"
            />
            <p style={{ margin: 0, fontSize: 14 }}>{date}</p>
            <span>·</span>
            <p style={{ margin: 0, fontSize: 14 }}>{a ? `${time} ${a}` : time}</p>
            <span>·</span>
            <TranslatedText
              translateKey={`homeworks.list.listItem.tags.${text}`}
              style={{ fontSize: 14, fontWeight: 600, color }}
              as="span"
            />
          </FlexRow>
        </FlexColumn>
      </FlexRow>
      <FlexRow flexWrap="wrap" gap={8}>
        {homework.assigned.map((assign) => {
          return (
            <HomeworkListItemUnitCard
              key={`${assign.id}-${assign.unit.id}`}
              id={homework.id}
              unit={assign.unit}
              lastExecution={assign.lastExecution}
              executionResult={assign.lastCompletedExecutionResult}
              timeToAllowContinueUnit={company?.timeToAllowContinueUnit}
            />
          );
        })}
      </FlexRow>
    </FlexColumn>
  );
}

HomeworkListItem.propTypes = {
  homework: PropTypes.shape({
    title: PropTypes.string,
    createdBy: PropTypes.string,
    group: PropTypes.string,
    dueDate: PropTypes.string,
    isNextInSequence: PropTypes.bool,
    assigned: PropTypes.arrayOf(
      PropTypes.shape({
        unit: PropTypes.shape({
          name: PropTypes.string,
          defaultFinishedAt: PropTypes.string,
          lastExecutionStartedAt: PropTypes.string,
          lastExecutionCompletedAt: PropTypes.string,
          lastExecutionLastActionAt: PropTypes.string,
          unitReview: PropTypes.shape({
            availableAt: PropTypes.string,
          }),
        }).isRequired,
        lastCompletedExecutionResult: PropTypes.shape({
          completedAt: PropTypes.string,
        }),
        lastExecutionResult: PropTypes.shape({
          completedAt: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
};

export default withRouter(HomeworkListItem);
